<template>
  <div
    class="relative aspect-video w-full touch-pan-y select-none overflow-hidden bg-gray-50 cursor-grab active:cursor-grabbing"
    @touchstart="startDrag"
    @touchmove="moveDrag"
    @touchend="endDrag"
    @touchcancel="cancelDrag"
    @mousedown="startMouseDrag"
    @dragstart.prevent
  >
    <div
      class="flex h-full w-full will-change-transform"
      :class="transitionClass"
      :style="trackStyle"
      @transitionend="handleTrackTransitionEnd"
    >
      <component
        v-for="slide in sliderItems"
        :key="slide.key"
        :is="slide.banner.url ? 'a' : 'div'"
        :href="slide.banner.url || null"
        :data-track-index="slide.trackIndex"
        :data-banner-index="slide.realIndex"
        class="relative block h-full w-full min-w-full flex-none"
        @click="handleClick($event, slide.banner)"
      >
        <video
          v-if="isVideo(slide.banner)"
          :src="getMediaUrl(slide.banner)"
          :poster="getPosterUrl(slide.banner)"
          class="h-full w-full object-contain bg-gray-50"
          muted
          autoplay
          playsinline
          webkit-playsinline
          preload="auto"
          @ended="handleMediaEnded(slide.realIndex)"
          @error="handleMediaIssue(slide.realIndex)"
          @stalled="handleMediaIssue(slide.realIndex)"
          @loadeddata="handleVideoLoaded(slide.realIndex)"
        />
        <img
          v-else
          :src="getMediaUrl(slide.banner)"
          class="h-full w-full object-contain bg-gray-50"
          :alt="slide.banner.name || slide.banner.img || 'banner'"
          draggable="false"
          @error="handleImageError($event, slide.banner)"
        />
      </component>
    </div>

    <div
      v-if="banners.length > 1"
      class="absolute bottom-2 left-0 right-0 flex justify-center gap-1"
    >
      <button
        v-for="(banner, index) in banners"
        :key="banner.id || index"
        type="button"
        class="h-1.5 rounded-full transition-all"
        :class="index === activeIndex ? 'w-5 bg-primary' : 'w-1.5 bg-gray-300'"
        :aria-label="`切換至第 ${index + 1} 張 banner`"
        @click="select(index)"
      />
    </div>
  </div>
</template>

<script>
const IMAGE_DELAY = 3000;

export default {
  props: {
    banners: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeIndex: 0,
      trackIndex: 0,
      timer: null,
      mountedReady: false,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragOffset: 0,
      suppressClick: false,
      snapTransition: false,
      transitioning: false,
      transitionFallback: null,
    };
  },
  computed: {
    activeBanner() {
      return this.banners[this.activeIndex] || null;
    },
    sliderItems() {
      const slides = this.banners.map((banner, index) => ({
        banner,
        realIndex: index,
        trackIndex: this.banners.length > 1 ? index + 1 : index,
        key: `real-${banner.id || index}`,
      }));

      if (this.banners.length <= 1) {
        return slides;
      }

      const first = slides[0];
      const last = slides[slides.length - 1];

      return [
        {
          banner: last.banner,
          realIndex: last.realIndex,
          trackIndex: 0,
          key: `clone-last-${last.banner.id || last.realIndex}`,
        },
        ...slides,
        {
          banner: first.banner,
          realIndex: first.realIndex,
          trackIndex: this.banners.length + 1,
          key: `clone-first-${first.banner.id || first.realIndex}`,
        },
      ];
    },
    trackStyle() {
      return {
        transform: `translate3d(calc(${-this.trackIndex * 100}% + ${this.dragOffset}px), 0, 0)`,
      };
    },
    transitionClass() {
      return this.dragging || this.snapTransition
        ? "transition-none"
        : "transition-transform duration-500 ease-out";
    },
  },
  watch: {
    banners: {
      immediate: true,
      handler() {
        this.clearTransitionFallback();
        this.transitioning = false;
        this.snapTransition = false;
        this.dragging = false;
        this.dragOffset = 0;

        if (this.activeIndex >= this.banners.length) {
          this.activeIndex = 0;
        }

        this.trackIndex = this.toTrackIndex(this.activeIndex);

        if (this.mountedReady) {
          this.scheduleActive();
        }
      },
    },
    activeIndex() {
      if (this.mountedReady && !this.transitioning) {
        this.scheduleActive();
      }
    },
  },
  mounted() {
    this.mountedReady = true;
    this.trackIndex = this.toTrackIndex(this.activeIndex);
    this.scheduleActive();
  },
  beforeDestroy() {
    this.clearTimer();
    this.clearTransitionFallback();
    this.removeMouseDragListeners();
  },
  methods: {
    encodeMediaUrl(url) {
      if (!url || typeof url !== "string") return url || "";
      try {
        // Encode path segments so Chinese / reserved chars (& etc.) work in <img>/<video src>
        const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://game-853.com");
        parsed.pathname = parsed.pathname
          .split("/")
          .map((segment) => {
            if (!segment) return segment;
            try {
              return encodeURIComponent(decodeURIComponent(segment));
            } catch (e) {
              return encodeURIComponent(segment);
            }
          })
          .join("/");
        return parsed.toString();
      } catch (e) {
        return url;
      }
    },
    getMediaUrl(banner) {
      return this.encodeMediaUrl(banner.media_url || banner.img || "");
    },
    getPosterUrl(banner) {
      const poster = banner.prd_logo || banner.poster || "";
      return poster ? this.encodeMediaUrl(poster) : undefined;
    },
    isVideo(banner) {
      if (!banner) return false;

      if (banner.media_type === "video") {
        return true;
      }

      if (banner.mime_type && banner.mime_type.indexOf("video/") === 0) {
        return true;
      }

      return /\.mp4($|\?)/i.test(banner.media_url || banner.img || "");
    },
    toTrackIndex(index) {
      return this.banners.length > 1 ? index + 1 : index;
    },
    handleClick(event, banner) {
      if (this.suppressClick || !banner || !banner.url) {
        event.preventDefault();
      }
    },
    select(index) {
      if (index === this.activeIndex) {
        this.scheduleActive();
        return;
      }

      this.clearTimer();
      this.clearTransitionFallback();
      this.transitioning = false;
      this.snapTransition = false;
      this.dragging = false;
      this.dragOffset = 0;
      this.snapTransition = true;
      this.activeIndex = index;
      this.snapToTrackIndex(this.toTrackIndex(index), this.scheduleActive);
    },
    scheduleActive() {
      this.clearTimer();

      if (this.dragging || this.transitioning || this.snapTransition) return;
      if (!this.activeBanner) return;

      if (this.isVideo(this.activeBanner)) {
        this.$nextTick(this.playVideo);

        const duration = Number(this.activeBanner.duration_seconds);
        // Always schedule an advance for videos so a failed play() cannot freeze the carousel.
        const waitMs =
          Number.isFinite(duration) && duration > 0
            ? duration * 1000 + 1000
            : IMAGE_DELAY;
        this.timer = setTimeout(this.advance, waitMs);

        return;
      }

      if (this.banners.length > 1) {
        this.timer = setTimeout(this.advance, IMAGE_DELAY);
      }
    },
    playVideo() {
      this.pauseAllVideos();

      const video = this.getActiveVideo();
      if (!video) {
        this.advanceAfterDelay();
        return;
      }

      try {
        video.currentTime = 0;
      } catch (e) {
        // Some browsers reject currentTime changes before metadata is ready.
      }

      const play = video.play && video.play();
      if (play && play.catch) {
        play.catch(() => {
          // Keep the poster/frame visible; still advance via scheduleActive timer.
        });
      }
    },
    handleVideoLoaded(realIndex) {
      if (realIndex === this.activeIndex && !this.dragging && !this.transitioning) {
        this.playVideo();
      }
    },
    handleImageError(event, banner) {
      const fallback = banner && banner.prd_logo;
      if (fallback && event && event.target && event.target.src !== fallback) {
        event.target.src = this.encodeMediaUrl(fallback);
        return;
      }
      if (event && event.target) {
        event.target.style.visibility = "hidden";
      }
    },
    advanceAfterDelay() {
      this.clearTimer();
      this.timer = setTimeout(this.advance, IMAGE_DELAY);
    },
    handleMediaEnded(realIndex) {
      if (realIndex === this.activeIndex) {
        this.advance();
      }
    },
    handleMediaIssue(realIndex) {
      if (realIndex === this.activeIndex) {
        this.advanceAfterDelay();
      }
    },
    advance() {
      this.clearTimer();

      if (this.banners.length <= 1) {
        this.scheduleActive();
        return;
      }

      if (this.transitioning) return;

      const currentIndex = this.activeIndex;
      const nextIndex = (currentIndex + 1) % this.banners.length;
      const nextTrackIndex =
        currentIndex === this.banners.length - 1
          ? this.banners.length + 1
          : this.toTrackIndex(nextIndex);

      this.startSlideTransition(nextIndex, nextTrackIndex);
    },
    previous() {
      this.clearTimer();

      if (this.banners.length <= 1) {
        this.scheduleActive();
        return;
      }

      if (this.transitioning) return;

      const currentIndex = this.activeIndex;
      const previousIndex = (currentIndex - 1 + this.banners.length) % this.banners.length;
      const previousTrackIndex = currentIndex === 0 ? 0 : this.toTrackIndex(previousIndex);

      this.startSlideTransition(previousIndex, previousTrackIndex);
    },
    handleTrackTransitionEnd(event) {
      if (event.target !== event.currentTarget || this.banners.length <= 1) return;
      if (!this.transitioning) return;

      this.finishSlideTransition();
    },
    startSlideTransition(activeIndex, trackIndex) {
      this.clearTransitionFallback();
      this.transitioning = true;
      this.activeIndex = activeIndex;
      this.trackIndex = trackIndex;
      this.transitionFallback = setTimeout(this.finishSlideTransition, 650);
    },
    finishSlideTransition() {
      this.clearTransitionFallback();

      let snapped = false;

      if (this.trackIndex === 0) {
        this.snapToTrackIndex(this.banners.length, this.scheduleActive);
        snapped = true;
      } else if (this.trackIndex === this.banners.length + 1) {
        this.snapToTrackIndex(1, this.scheduleActive);
        snapped = true;
      } else {
        this.trackIndex = this.toTrackIndex(this.activeIndex);
      }

      this.transitioning = false;

      if (!snapped) {
        this.scheduleActive();
      }
    },
    snapToTrackIndex(index, callback) {
      this.snapTransition = true;
      this.trackIndex = index;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.snapTransition = false;
            if (callback) {
              callback();
            }
          });
        });
      });
    },
    clearTransitionFallback() {
      if (!this.transitionFallback) return;
      clearTimeout(this.transitionFallback);
      this.transitionFallback = null;
    },
    clearTimer() {
      if (!this.timer) return;
      clearTimeout(this.timer);
      this.timer = null;
    },
    getActiveVideo() {
      const slide = this.$el.querySelector(`[data-track-index="${this.trackIndex}"]`);

      return slide ? slide.querySelector("video") : null;
    },
    pauseAllVideos() {
      this.$el.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
    },
    startDrag(event) {
      if (
        this.banners.length <= 1 ||
        this.transitioning ||
        this.snapTransition ||
        !event.touches ||
        !event.touches.length
      ) {
        return;
      }

      this.beginDrag(event.touches[0].clientX, event.touches[0].clientY);
    },
    moveDrag(event) {
      if (!this.dragging || !event.touches || !event.touches.length) return;

      this.updateDrag(event.touches[0].clientX, event.touches[0].clientY);
    },
    startMouseDrag(event) {
      if (
        this.banners.length <= 1 ||
        this.transitioning ||
        this.snapTransition ||
        event.button !== 0
      ) {
        return;
      }

      event.preventDefault();
      this.beginDrag(event.clientX, event.clientY);
      window.addEventListener("mousemove", this.moveMouseDrag);
      window.addEventListener("mouseup", this.endMouseDrag);
    },
    moveMouseDrag(event) {
      if (!this.dragging) return;

      event.preventDefault();
      this.updateDrag(event.clientX, event.clientY);
    },
    endMouseDrag() {
      this.removeMouseDragListeners();
      this.endDrag();
    },
    removeMouseDragListeners() {
      window.removeEventListener("mousemove", this.moveMouseDrag);
      window.removeEventListener("mouseup", this.endMouseDrag);
    },
    beginDrag(clientX, clientY) {
      this.clearTimer();
      this.dragging = true;
      this.dragStartX = clientX;
      this.dragStartY = clientY;
      this.dragOffset = 0;
    },
    updateDrag(clientX, clientY) {
      const offsetX = clientX - this.dragStartX;
      const offsetY = clientY - this.dragStartY;

      if (Math.abs(offsetY) > Math.abs(offsetX) && Math.abs(offsetY) > 12) {
        this.dragOffset = 0;
        return;
      }

      this.dragOffset = offsetX;
    },
    endDrag() {
      if (!this.dragging) return;

      const width = this.$el.clientWidth || 1;
      const threshold = Math.min(80, width * 0.18);
      const offset = this.dragOffset;
      const shouldChange = Math.abs(offset) >= threshold;

      this.dragging = false;
      this.dragOffset = 0;

      if (Math.abs(offset) > 8) {
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 300);
      }

      if (!shouldChange) {
        this.scheduleActive();
        return;
      }

      if (offset < 0) {
        this.advance();
      } else {
        this.previous();
      }
    },
    cancelDrag() {
      if (!this.dragging) return;

      this.removeMouseDragListeners();
      this.dragging = false;
      this.dragOffset = 0;
      this.scheduleActive();
    },
  },
};
</script>
